/**
 * When it is safe to switch a tablet over to a newer version of the site.
 *
 * A forklift tablet keeps one page open for days. The offline copy it runs
 * from was only ever replaced when the page loaded, so such a tablet stayed on
 * the old version indefinitely (seen 2026-10-09: 17 minutes on the sign-in
 * screen after a deploy, updated only when reloaded). So the page now looks
 * for a newer version by itself, on a timer.
 *
 * Taking it means reloading the page, and a reload throws away whatever is on
 * the screen: pallets on the forks, a trip half loaded, a field half typed.
 * That must never happen to a driver in the middle of something. So the newer
 * version WAITS, however long, until all of these are true:
 *
 *  - the tablet is on a resting screen — nothing on it to lose;
 *  - no offline transaction is waiting to be sent or on its way;
 *  - no scan is waiting for Wi-Fi;
 *  - nobody has touched the screen or scanned for a little while.
 *
 * Pure, so the rule is tested without a browser.
 */

/** Screens with nothing on them a reload could lose. Everything else is work in progress. */
export const RESTING_SCREENS = ['/', '/login', '/settings'] as const

/** How long the tablet must have been left alone. */
export const QUIET_BEFORE_UPDATE_MS = 20_000

export type UpdateMoment = {
  /** The screen the tablet is on. */
  path: string
  /** Offline transactions waiting to be sent. */
  pending: number
  /** Whether one is on its way to the server right now. */
  sending: boolean
  /** Whether any scan is waiting for Wi-Fi. */
  scansWaiting: boolean
  /** How long since anyone tapped, typed or scanned. */
  quietMs: number
}

export type UpdateVerdict = { safe: true } | { safe: false, waitingFor: string }

export function safeToUpdate(moment: UpdateMoment): UpdateVerdict {
  if (!(RESTING_SCREENS as readonly string[]).includes(moment.path)) {
    return { safe: false, waitingFor: 'the driver to finish on this screen' }
  }
  if (moment.pending > 0 || moment.sending) {
    return { safe: false, waitingFor: 'offline transactions to be sent' }
  }
  if (moment.scansWaiting) {
    return { safe: false, waitingFor: 'scans that are waiting for Wi-Fi' }
  }
  if (moment.quietMs < QUIET_BEFORE_UPDATE_MS) {
    return { safe: false, waitingFor: 'the tablet to be left alone for a moment' }
  }
  return { safe: true }
}

/** The moment this copy of the site was built, as text for a person; '' if it cannot be read. */
export function describeBuild(stamp: string, locale?: string): string {
  const built = new Date(stamp)
  if (Number.isNaN(built.getTime())) return ''
  return built.toLocaleString(locale ?? [], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
